import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, inject, useTemplateRef, onMounted, nextTick } from 'vue';
    import Invoice from '../../module/common/models/Invoice.ts';
    import File from '../../module/common/models/File.ts';
    import { merge_invoice_pdfs, render_pdf, stamp } from '../../module/pdf/module.ts';
    import Error from '../../module/error_handler/types/Error.ts';
    import { ErrorClass, ErrorZone } from '../../module/error_handler/types/enums.ts';
    import { MainStore } from '../../store.ts';
    import Uuid from '../../module/common/models/Uuid.ts';
    import { remove_page_from_pdf } from '../../module/pdf/pdf_writer.ts';
    import _ from 'lodash'
    import { watchDebounced } from '@vueuse/core';

    const scale = 1.3333333333333333333333333333;
    interface Props {
        file: File | null,
        invoices: Invoice[],
        is_stamping: boolean
    }

    
export default /*@__PURE__*/_defineComponent({
  __name: 'pdf_viewer',
  props: {
    file: { type: [Object, null], required: true },
    invoices: { type: Array, required: true },
    is_stamping: { type: Boolean, required: true }
  },
  emits: ['stamp_pdf', 'remove_pdf_page'],
  setup(__props: any, { expose: __expose, emit: __emit }) {
  __expose();

    const store: MainStore = inject('store') as MainStore

    const emit = __emit
    const props = __props
    
    const file = computed(() => props.file)
    const is_stamping = computed(() => props.is_stamping)
    const invoices = computed(() => props.invoices)
    const dat_is_loading = ref(false)
    const dat_page_to_delete = ref(-1)
    const dat_force_refresh = ref(false)

    const canvas_container = useTemplateRef('canvas-container')
    const tracking_area = useTemplateRef('tracking-area')
    const rectangle = useTemplateRef('floating-rectangle')

    function clear_pdf() {
        if (canvas_container.value !== null) {
            canvas_container.value.innerHTML = ""
        }
    }
    
    async function stamp_pdf(arg_event: MouseEvent) {
        const target = arg_event.target as HTMLInputElement;

        if (!is_stamping.value
            || invoices.value.length !== 1
            || file.value === null
        ) {
            return
        }

        const invoice = invoices.value[0]

        if (invoice.pdf_bytes === null) {
            return
        }

        const draw_x = (arg_event.layerX - target.offsetLeft) - 140
        const draw_y = Math.floor((1056 - (arg_event.layerY - target.offsetTop))) - 46 - 9

        try {
            const stamped_pdf_bytes = await stamp(draw_x, draw_y, invoice.invoice_information, invoice.pdf_bytes)

            emit('stamp_pdf', file.value, invoice, stamped_pdf_bytes, draw_x, draw_y)
        } catch (original_error) {
            if ("id" in original_error
                && "error_message" in original_error
                && "error_class" in original_error
            ) {
                const error: Error = original_error
                error.error_zone = ErrorZone.pdf_viewer

                store.log_error(error)
            } else {
                throw original_error
            }
        }
    }

    const ref_dialog = useTemplateRef('dialog')

    function cancel_delete_page() {
        const loc_dialog: HTMLDialogElement | null = ref_dialog.value
        
        if (loc_dialog === null) {
            return
        }

        dat_page_to_delete.value = -1
        loc_dialog.close()
    }


    function ask_remove_page(arg_page_index: number) {
        dat_page_to_delete.value = arg_page_index

        const loc_dialog: HTMLDialogElement | null = ref_dialog.value
        if (loc_dialog === null) {
            return
        }

        loc_dialog.showModal()    
    }

    async function confirm_delete_page() {
        const loc_page_index = dat_page_to_delete.value

        const loc_dialog: HTMLDialogElement | null = ref_dialog.value
        if (loc_dialog === null) {
            return
        }
        dat_page_to_delete.value = -1
        loc_dialog.close()

        const loc_invoice = invoices.value[0]
        const loc_file = file.value

        if (invoices.value.length !== 1
            || file.value === null
            || loc_page_index < 0
            || loc_invoice.pdf_bytes === null
        ) {
            return
        }

        try {

            let loc_stamped_pdf_bytes: Uint8Array<ArrayBufferLike>|null = null
            const loc_pdf_bytes = await remove_page_from_pdf(loc_invoice.pdf_bytes, loc_page_index)
            if (loc_invoice.stamped && loc_invoice.stamped_pdf_bytes !== null) {
                loc_stamped_pdf_bytes = await remove_page_from_pdf(loc_invoice.stamped_pdf_bytes, loc_page_index)
            }
            
            dat_force_refresh.value = true
            emit('remove_pdf_page', loc_file, loc_invoice, loc_pdf_bytes, loc_stamped_pdf_bytes)
            
        } catch (original_error) {
            if ("id" in original_error
                && "error_message" in original_error
                && "error_class" in original_error
            ) {
                const error: Error = original_error
                error.error_zone = ErrorZone.pdf_viewer

                store.log_error(error)
            } else {
                throw original_error
            }
        }
    }

    async function render(arg_pdf_bytes: Uint8Array<ArrayBufferLike>, arg_merging_pdfs: boolean) {
        if (canvas_container.value !== null) {
            try {
                dat_is_loading.value = true
                await render_pdf(arg_pdf_bytes, canvas_container.value)
            } catch (original_error) {
                if ("id" in original_error
                    && "error_message" in original_error
                    && "error_class" in original_error
                ) {
                    const error: Error = original_error
                    error.error_zone = ErrorZone.pdf_viewer

                    store.log_error(error)
                } else {
                    throw original_error
                }
            } finally {
                dat_is_loading.value = false
            }

            try {
                if (canvas_container.value.children.length > 0) {
                    const loc_canvas_children_length = canvas_container.value.children.length
                    for (let tmp_child_index = 0;tmp_child_index < loc_canvas_children_length; tmp_child_index++) {
                        if (tmp_child_index === 0) {
                            const first_canvas = canvas_container.value.children[tmp_child_index]
                            first_canvas.addEventListener('click', (tmp_event: Event) => stamp_pdf(tmp_event as MouseEvent));
                        } else if (!arg_merging_pdfs) {
                            const tmp_canvas = canvas_container.value.children[tmp_child_index]
                            nextTick(() => {
                                let tmp_button = document.createElement('button')
                                tmp_button.classList = "error disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-200 flex w-full items-center justify-center gap-1 rounded-full border border-gray-300 bg-white text-sm font-medium text-gray-700 shadow-theme-xs hover:bg-gray-50 hover:text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03] dark:hover:text-gray-200 lg:inline-flex px-3 py-3"            
                                tmp_button.innerText = "Delete Page"
                                tmp_canvas.before(tmp_button)
                                tmp_button.addEventListener('click', (tmp_event: Event) => ask_remove_page(tmp_child_index));
                            })
                        }
                    }
                }
            } catch (original_error) {
                const error: Error = {
                    id: new Uuid(),
                    error_message: "UNKNOWN: pdf_viewer.vue:render canvas_container has no children ",
                    error_class: ErrorClass.developer,
                    error_zone: ErrorZone.pdf_viewer,
                    thrown_error: original_error,
                    extra_data: {}
                }

                store.log_error(error)
            }
        }
    }

    onMounted(() => {
        if (tracking_area.value === null
            || rectangle.value === null
        ) {
            return
        }

        tracking_area.value.addEventListener('mouseenter', () => {
            if (rectangle.value === null) {
                return
            }

            if (is_stamping.value) {
                rectangle.value.style.display = 'block';
            }
        });

        tracking_area.value.addEventListener('mousemove', (e) => {
            if (tracking_area.value === null
                || rectangle.value === null
            ) {
                return
            }

            if (is_stamping.value) {
                const rect = tracking_area.value.getBoundingClientRect();
                const x = e.clientX - rect.left - 141;
                const y = e.clientY - rect.top - 46;

                rectangle.value.style.left = `${x}px`;
                rectangle.value.style.top = `${y}px`;
            }
        });

        tracking_area.value.addEventListener('mouseleave', () => {
            if (rectangle.value === null) {
                return
            }
            rectangle.value.style.display = 'none';
        });
    })

    const run_watch_invoices = _.debounce((arg_invoices: Invoice[], arg_old_invoices: Invoice[]) => {
        if (file.value === null) {
            clear_pdf()
            return
        }

        if (arg_invoices.length <= 0) {
            clear_pdf()
            return
        } else if (arg_invoices.length === 1) {
            if (arg_old_invoices.length === 1 
                && arg_old_invoices[0].id.get() == arg_invoices[0].id.get()
                && !arg_old_invoices[0].stamped
                && !arg_invoices[0].stamped
                && !dat_force_refresh.value
            ) {
                return
            }

            dat_force_refresh.value = false

            const pdf_bytes = arg_invoices[0].pdf_bytes
            const stamped_pdf_bytes = arg_invoices[0].stamped_pdf_bytes

            clear_pdf()
            if (stamped_pdf_bytes !== null) {
                render(stamped_pdf_bytes, false)
            } else if (pdf_bytes !== null) {
                render(pdf_bytes, false)
            }
        } else {
            clear_pdf()

            merge_invoice_pdfs(arg_invoices, false).then((merged_pdf_bytes) => {
                if (file.value === null) {
                    return
                }

                render(merged_pdf_bytes, true)
            })
        }
    }, 100)
    

    watchDebounced(
        invoices,
        (arg_invoices: Invoice[], arg_old_invoices: Invoice[]) => {
            if (file.value === null) {
                clear_pdf()
                return
            }

            let first_old_invoice: Invoice|null = null
            let first_invoice: Invoice|null = null

            if (arg_old_invoices
                && arg_old_invoices.length === 1
            ) {
                first_old_invoice = arg_old_invoices[0]
            }

             if (arg_invoices
                && arg_invoices.length === 1
            ) {
                first_invoice = arg_invoices[0]
            }

            if (arg_invoices.length <= 0) {
                clear_pdf()
                return
            } else if (first_invoice !== null) {
                if (first_old_invoice !== null
                    && first_old_invoice.id.get() == first_invoice.id.get()
                    && !first_old_invoice.stamped
                    && !first_invoice.stamped
                    && !dat_force_refresh.value
                ) {
                    return
                }

                dat_force_refresh.value = false

                const pdf_bytes = first_invoice.pdf_bytes
                const stamped_pdf_bytes = first_invoice.stamped_pdf_bytes

                clear_pdf()
                if (stamped_pdf_bytes !== null) {
                    render(stamped_pdf_bytes, false)
                } else if (pdf_bytes !== null) {
                    render(pdf_bytes, false)
                }
            } else {
                clear_pdf()

                merge_invoice_pdfs(arg_invoices, false).then((merged_pdf_bytes) => {
                    if (file.value === null) {
                        return
                    }

                    render(merged_pdf_bytes, true)
                })
            }
        }, 
        {
            immediate: true, 
            deep: true,
            debounce: 150,
            maxWait: 1000
        }
    )

    watch(is_stamping, (arg_is_stamping: boolean) => {
        if (!arg_is_stamping) {
            if (rectangle.value === null) {
                return
            } 

            rectangle.value.style.display = 'none';
        }
    })
 

const __returned__ = { scale, store, emit, props, file, is_stamping, invoices, dat_is_loading, dat_page_to_delete, dat_force_refresh, canvas_container, tracking_area, rectangle, clear_pdf, stamp_pdf, ref_dialog, cancel_delete_page, ask_remove_page, confirm_delete_page, render, run_watch_invoices }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})